import api, { getAssetUrl } from './api';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_PAGE_SIZE = 100;
const RESULT_FILTER_OPTIONS = ['Entrada liberada', 'Saída liberada', 'Acesso negado', 'Desconhecido'];

export async function fetchMonitorData(filters = {}) {
  const response = await api.get('/controle-acesso/registros', { params: buildRegistroParams(filters) });
  const registros = response.data?.items || [];
  const events = registros.map(normalizeRegistro).sort(compareEventsDesc);

  return {
    events,
    stats: buildStats(response.data?.stats || {}, events),
    filterOptions: buildFilterOptions(events),
    total: response.data?.total || events.length,
  };
}

export function getMonitorEventKey(event) {
  if (event.eventFaceId) {
    return `${event.deviceId || 'sem-dispositivo'}:${event.eventFaceId}`;
  }
  return `registro:${event.id}`;
}

export function mergeMonitorEvents(currentEvents, fetchedEvents) {
  const currentByKey = new Map(currentEvents.map((event) => [getMonitorEventKey(event), event]));
  const fetchedByKey = new Map(fetchedEvents.map((event) => [getMonitorEventKey(event), event]));
  const newKeys = [];
  let changed = currentEvents.length !== fetchedEvents.length;

  fetchedByKey.forEach((event, key) => {
    const current = currentByKey.get(key);
    if (!current) {
      currentByKey.set(key, { ...event, isNew: true });
      newKeys.push(key);
      changed = true;
      return;
    }

    const merged = { ...current, ...event, isNew: current.isNew };
    if (hasMeaningfulEventChange(current, merged)) {
      currentByKey.set(key, merged);
      changed = true;
    }
  });

  currentByKey.forEach((_, key) => {
    if (!fetchedByKey.has(key)) {
      currentByKey.delete(key);
      changed = true;
    }
  });

  if (!changed) {
    return { events: currentEvents, newKeys, changed: false };
  }

  return {
    events: [...currentByKey.values()].sort(compareEventsDesc),
    newKeys,
    changed: true,
  };
}

export function clearNewEventHighlights(events, keys) {
  const keySet = new Set(keys);
  let changed = false;
  const nextEvents = events.map((event) => {
    if (!event.isNew || !keySet.has(getMonitorEventKey(event))) {
      return event;
    }
    changed = true;
    return { ...event, isNew: false };
  });
  return changed ? nextEvents : events;
}

function buildRegistroParams(filters) {
  const hasSearch = hasActiveFilters(filters);

  return {
    data_inicial: filters.startDate || undefined,
    data_final: filters.endDate || undefined,
    nome: filters.search?.trim() || undefined,
    dispositivo: filters.device || undefined,
    status: mapResultToStatus(filters.result),
    sentido: filters.result === 'Saída liberada' ? 'saida' : filters.result === 'Entrada liberada' ? 'entrada' : undefined,
    page: 1,
    page_size: hasSearch ? SEARCH_PAGE_SIZE : DEFAULT_PAGE_SIZE,
  };
}

function hasActiveFilters(filters = {}) {
  return Boolean(
    filters.search?.trim()
    || filters.group
    || filters.device
    || filters.userType
    || filters.result
    || filters.startDate
    || filters.endDate
  );
}

function normalizeRegistro(registro) {
  const dispositivo = registro.dispositivo || null;
  const deviceId = registro.dispositivo_id || dispositivo?.id || null;
  const groupName = registro.bloco_torre || getPrimaryGroupName(dispositivo);
  const rawStatus = registro.status || 'desconhecido';

  return {
    id: registro.id,
    eventFaceId: registro.evento_facial_id || null,
    deviceId,
    registeredPhotoUrl: getAssetUrl(registro.foto_cadastrada_url),
    capturedPhotoUrl: getAssetUrl(registro.foto_capturada_url || registro.captured_photo_url),
    userName: registro.nome_pessoa_facial || 'Desconhecido',
    facialCode: registro.codigo_pessoa_facial || '',
    userType: formatUserType(registro.pessoa_tipo_sistema || registro.tipo_pessoa),
    apartment: registro.pessoa_unidade || '-',
    tower: groupName || '-',
    occurredAt: registro.data_hora_acesso,
    deviceName: dispositivo?.nome || registro.ip_facial || '-',
    deviceModel: dispositivo?.marca || '-',
    deviceIp: registro.ip_facial || dispositivo?.ip || null,
    deviceStatus: '',
    authMethod: formatAuthMethod(registro.metodo || registro.tipo_evento),
    result: formatResult(rawStatus, dispositivo?.sentido),
    resultStatus: rawStatus,
    denialReason: registro.motivo_negativa || registro.denial_reason || null,
    locationName: groupName || dispositivo?.nome || '-',
    group: groupName || '',
  };
}

function buildStats(stats, events) {
  return {
    entriesToday: stats.autorizados || 0,
    deniedToday: stats.negados || 0,
    visitorsToday: events.filter((event) => event.userType === 'Visitante').length,
    onlineDevices: unique(events.map((event) => event.deviceId).filter(Boolean)).length,
  };
}

function buildFilterOptions(events) {
  return {
    groups: unique(events.map((event) => event.group).filter(Boolean)),
    devices: unique(events.map((event) => event.deviceName).filter(Boolean).filter((value) => value !== '-')),
    userTypes: unique(events.map((event) => event.userType).filter(Boolean)),
    results: RESULT_FILTER_OPTIONS,
  };
}

function hasMeaningfulEventChange(current, next) {
  const fields = [
    'registeredPhotoUrl',
    'capturedPhotoUrl',
    'userName',
    'facialCode',
    'userType',
    'apartment',
    'tower',
    'occurredAt',
    'deviceName',
    'authMethod',
    'resultStatus',
    'result',
    'denialReason',
  ];
  return fields.some((field) => current[field] !== next[field]);
}

function compareEventsDesc(a, b) {
  const aTime = new Date(a.occurredAt || 0).getTime();
  const bTime = new Date(b.occurredAt || 0).getTime();
  if (bTime !== aTime) return bTime - aTime;
  return String(getMonitorEventKey(b)).localeCompare(String(getMonitorEventKey(a)));
}

function getPrimaryGroupName(dispositivo) {
  return dispositivo?.grupos?.[0]?.nome || '';
}

function formatUserType(value) {
  const normalized = String(value || '').toLowerCase();
  const labels = {
    morador: 'Morador',
    visitante: 'Visitante',
    contratado: 'Prestador',
    prestador: 'Prestador',
    desconhecido: 'Desconhecido',
  };
  return labels[normalized] || 'Desconhecido';
}

function formatAuthMethod(value) {
  if (value === 'lpr') return 'Placa (LPR)';
  if (value === 'facial_rtsp') return 'Facial por câmera';
  const normalized = String(value || '').toLowerCase();
  const labels = {
    entry: 'Entrada',
    entrada: 'Entrada',
    abertura_remota: 'Abertura remota',
    remote_open: 'Abertura remota',
    exit: 'Saída',
    saida: 'Saída',
    saída: 'Saída',
    face: 'Face',
    facial: 'Face',
    cartao: 'Cartão',
    card: 'Cartão',
    senha: 'Senha',
    password: 'Senha',
    qrcode: 'QR Code',
    qr_code: 'QR Code',
    placa: 'Placa',
    plate: 'Placa',
  };
  return labels[normalized] || formatFallbackLabel(value);
}

function formatResult(value, sentido) {
  const normalized = String(value || '').toLowerCase();
  if (sentido === 'saida' && ['autorizado', 'liberado'].includes(normalized)) return 'Saída liberada';
  const labels = {
    autorizado: 'Entrada liberada',
    liberado: 'Entrada liberada',
    negado: 'Acesso negado',
    desconhecido: 'Desconhecido',
  };
  return labels[normalized] || formatFallbackLabel(value);
}

function mapResultToStatus(value) {
  if (value === 'Entrada liberada' || value === 'Saída liberada') return 'autorizado';
  if (value === 'Acesso negado') return 'negado';
  if (value === 'Desconhecido') return 'desconhecido';
  return undefined;
}

function formatFallbackLabel(value) {
  if (!value) return 'Desconhecido';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function unique(values) {
  return [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));
}
